import { useDisclosure } from '@heroui/modal';
import { useCallback, FocusEvent, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Input } from '@heroui/input';
import { Button } from '@heroui/button';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@heroui/alert';
import { useTranslation } from 'react-i18next';
import { MdEdit } from 'react-icons/md';

import { useEditSong, useSong, useSongbook } from '@/hooks/useSongbook.ts';
import Container from '@/components/Container/Container.tsx';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.tsx';
import DefaultLayout from '@/layouts/default.tsx';
import { songbookBg } from '@/core/color.ts';
import { songName } from '@/core/song.ts';

import ExportButton from '../components/ExportButton.tsx';

import SongMedia from './SongMedia.tsx';
import SongEditModal from './SongEditModal.tsx';
import SongMediaModal from './SongMediaModal.tsx';
import SongNavigation from './SongNavigation.tsx';
import ImportButton from '@/pages/Songbook/components/ImportButton.tsx';

function SongDetail() {
  const { t } = useTranslation();
  const { songbookId = '', songId = '' } = useParams();
  const { isLoading: isSongbookLoading, data: songbook, error: songbookError } = useSongbook(songbookId);
  const { isLoading: isSongLoading, data: song, error: songError } = useSong(songId);
  const { isPending: isEditPending, mutateAsync: editSong } = useEditSong(songId);
  const navigate = useNavigate();
  const editDisclosure = useDisclosure();
  const addMediaDisclosure = useDisclosure();
  const [title, setTitle] = useState<string>('');
  const [artist, setArtist] = useState<string>('');

  const handleDetailChange = useCallback(async (e: FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue.trim() !== '') {
      await editSong({ [e.target.name]: newValue });
    }
  }, [editSong]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setTitle(song?.title || ''), [song?.title]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setArtist(song?.artist || ''), [song?.artist]);

  if (isSongbookLoading || isSongLoading) {
    return (
      <DefaultLayout>
        <div className="bg-content2 h-20" />
      </DefaultLayout>
    );
  }

  if (songbookError || !songbook || songError || !song) {
    return (
      <DefaultLayout>
        <Container>
          <div className="flex justify-center my-8">
            <Alert
              className="sm:max-w-1/2"
              color="danger"
              description={t('songbook.song.errorDescription')}
              endContent={
                <Button color="danger" size="sm" variant="flat" onPress={() => navigate('/songbook')}>
                  {t('songbook.song.goBack')}
                </Button>
              }
              title={t('songbook.song.errorTitle')}
              variant="faded"
            />
          </div>
        </Container>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className={classNames('bg-content2', songbookBg(songbook))}>
        <Container>
          <div className="h-20 flex justify-between items-center">
            <h1 className="text-3xl">
              {`${songbook.title} - ${song.title}`}
            </h1>
            <div className="flex gap-2">
              <ExportButton
                color={songbook.color}
                songId={song.id}
                variant="solid"
              />
              <ImportButton
                color={songbook.color}
                songbookId={songbook.id}
                variant="solid"
              />
              <Button
                color={songbook.color}
                startContent={<MdEdit />}
                variant="solid"
                onPress={editDisclosure.onOpen}
              >
                {t('songbook.song.edit')}
              </Button>
            </div>
          </div>
        </Container>
      </div>
      <div className="flex grow flex-col">
        <Container>
          <div className="my-4">
            <Breadcrumbs
              items={[
                { href: '/songbook', value: 'Songbooks' },
                { href: `/songbook/${songbook.id}`, value: songbook.title },
                { href: `/songbook/${songbook.id}/song/${song.id}`, value: songName(song) },
              ]}
            />
          </div>
        </Container>
        <div className="px-4 sm:px-8 mx-auto w-full max-w-[1800px]">
          <SongNavigation song={song} />
          <div className="border-1 border-default-200 rounded-xl p-4 mb-4">
            <div className="mb-4 flex flex-col">
              <Input
                classNames={{ input: 'text-xl', inputWrapper: '!px-0', innerWrapper: 'py-0' }}
                name="artist"
                placeholder={t('songbook.song.fields.artist.placeholder')}
                readOnly={isEditPending}
                size="lg"
                value={artist}
                variant="underlined"
                onBlur={handleDetailChange}
                onChange={(e) => setArtist(e.target.value)}
              />
              <Input
                classNames={{ input: 'text-3xl', inputWrapper: '!px-0', innerWrapper: 'py-0' }}
                name="title"
                placeholder={t('songbook.song.fields.title.placeholder')}
                readOnly={isEditPending}
                size="lg"
                value={title}
                variant="underlined"
                onBlur={handleDetailChange}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <SongMedia editDisclosure={editDisclosure} mediaDisclosure={addMediaDisclosure} song={song} />
          </div>
        </div>
      </div>
      <SongEditModal disclosure={editDisclosure} song={song} />
      <SongMediaModal disclosure={addMediaDisclosure} song={song} />
    </DefaultLayout>
  );
}

export default SongDetail;
